/* global axios */
import Cookies from 'js-cookie';
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

class CacheEnabledApiClient extends ApiClient {
  // One store per account AND signed-in user. The client is a module
  // singleton built before routing settles (it created "cw-store-" with no
  // account), and a shared store served one agent's inboxes/teams/labels to
  // whoever logged in next on the same browser.
  get dataManager() {
    let uid = '';
    try {
      uid = JSON.parse(Cookies.get('cw_d_session_info') || '{}').uid || '';
    } catch {
      uid = '';
    }
    const key = `${this.accountIdFromRoute}-${uid}`;
    if (this.cachedDataManager?.key !== key) {
      this.cachedDataManager = { key, manager: new DataManager(key) };
    }
    return this.cachedDataManager.manager;
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    let cacheKeyFromApi;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${this.accountIdFromRoute}/cache_keys`
      );
      cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    } catch {
      // A failed freshness check (seen as a 401 mid account-switch) must not
      // leave the sidebar and teams empty — load straight from the network.
      return this.getFromNetwork();
    }
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await this.dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();

    try {
      await this.dataManager.initDb();

      this.dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await this.dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    if (!this.dataManager.db) {
      await this.dataManager.initDb();
    }

    const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
